import React, { useState, useEffect } from 'react';
import { useDispatch } from "react-redux";

import { FormControlLabel, FormLabel, Radio, RadioGroup, fade, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

import { setItem } from "../../actions";
import makePath from '../../utils/makePath';
import { useDirty, useFormElementCleanUp } from '../../hooks';
import { FormElementWrapper } from './FormElementWrapper';

const useStyles = makeStyles((theme) => ({
  formControl: {
    width: 'auto'
  },
  radioGroup: {
    flexDirection: 'row',
    margin: '0 0 0 -9px',
  },
  fieldset: {
    fontWeight: 500,
    paddingTop: 0,
    paddingBottom: 0,
    marginRight: 0,
    marginLeft: 0,
    minInlineSize: 'auto',
    border: 'none',
  },
  label: {
    border: 'none',
    marginBottom: 9,
    '&:focused': {
      color: theme.palette.neutral.dark2
    }
  },
  formControlLabel: {
    margin: 0,
    height: 40,
    width: 46,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& .MuiFormControlLabel-label': {
      position: 'absolute',
      height: '1px',
      width: '1px', 
      clip: 'rect(1px,1px,1px,1px)',
      clipPath: 'polygon(0px 0px, 0px 0px, 0px 0px)',
      overflow: 'hidden',
    }
  }
}));

// Radio component for the actual input element
const StyledRadio = React.memo((props) => {
  const useStyles = makeStyles({
    radio: {
      boxSizing: 'content-box',
      padding: 0,
      '&:hover': {
        background: 'none',
      }
    },
    icon: {
      borderRadius: '50%',
      width: '30px',
      height: '30px',
      backgroundColor: props.hex,
      border: '#fff solid',
      borderWidth: 2,
      transition: 'border-width .07s',
      'input:hover ~ &': {
        boxShadow: `0px 1px 3px ${fade('#000', .5)}`,
      },
    },
    checkedIcon: {
      borderWidth: 5,
      boxShadow: `0px 1px 3px ${fade('#000', .5)}`,
    },
  });

  const classes = useStyles();

  return (
    <Tooltip title={props.label}>
      <Radio
        className={classes.radio}
        disableRipple
        color="default"
        checkedIcon={<span className={classes.icon + ' ' + classes.checkedIcon} />}
        icon={<span className={classes.icon} />}
        {...props}
      />
    </Tooltip>
  );
})

// Group of radio elements constitutes the whole color picker
export const ColorPicker = React.memo(({callback,given,colors,label,name, ...props}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const setDirty = useDirty();
  
  // Keep track of chosen color by index
  const [colorIndex, setColorIndex] = useState('color-0');

  const path = makePath(props.path,name);  
  useFormElementCleanUp(path);
  
  // Set state from given value, if provided
  useEffect(() => {
    if (given && colors) {

      // Get index of given color
      colors.forEach((color,index) => {
        if (given === color.name) {
          setColorIndex('color-' + index);
        }
      })

    }
  }, [colors,given,path]);

  // Set new value in state when new radio is selected
  const handleRadioChange = (e) => {
    const { value } = e.target;
    setColorIndex(value);
    setDirty(true);
  }

  // Set color name in store when chosen color has changed in state
  useEffect(() => {
    const index = colorIndex.split('-')[1];
    const chosenColor = colors[index];
    dispatch(setItem(path, chosenColor.name));
    if (callback) {
      callback(name,chosenColor.name);
    }
  }, [colors,callback,colorIndex,dispatch,name,path])

  return (
    <FormElementWrapper component="fieldset" className={classes.fieldset}>
      
      <FormLabel component="legend" className={classes.label}>{label}</FormLabel>

      {/* Radio group natively allows only one color to be chosen at a time */}
      <RadioGroup className={classes.radioGroup} aria-label={label} name={name} value={colorIndex} onChange={handleRadioChange}>
        {
          colors.map((color, index) => (
            <FormControlLabel 
              key={index} 
              className={classes.formControlLabel} 
              value={'color-' + index} // used to keep track of color's index in state
              control={<StyledRadio hex={color.hex} label={color.label}/>} 
              label={color.name}
            />
          ))
        }
      </RadioGroup>

    </FormElementWrapper>
  );

})
